/**
 * useSitemapData hook — fetches from the sitemap-tree endpoint
 * and produces the interface shape the App component expects.
 *
 * - Calls SitemapApi.loadSitemapTree (not loadSitemapNavigation)
 * - Sorts using flat `children` array (not `children.list`)
 * - Finds invalid pages by walking the DTO tree directly
 * - pageAlias is accessed directly from TreeViewNode (no pathToAliasMap needed)
 */

import { events } from '@ddc-webplatform/cmsc-modules';
import { SitemapApi } from 'api';
import { isExternalUrl } from 'helpers/utils';
import { APP_CONTAINER } from 'constants/global-constants';
import { SitemapTreeData, SitemapState, PageData } from 'models';
import { useCallback, useEffect, useState } from 'react';
import { LOCALE } from 'constants/composer';

/** Event names for React ↔ ExtJS communication */
const EVENTS = {
	NAVIGATE_TO_PAGE: 'navigate-to-page',
	SELECT_PAGE: 'select-page'
};

const INITIAL_STATE: SitemapState = {
	sitemapData: null,
	invalidPages: [],
	isLoading: true,
	error: null
};

const getContainer = () => document.getElementById(APP_CONTAINER);

// ────────────────────────────────────────────────────────────────────────────
// Tree-walking helpers
// ────────────────────────────────────────────────────────────────────────────

const buildPath = (parentPath: string, segment: string): string => {
	if (segment) {
		return parentPath ? `${parentPath}/${segment}` : `/${segment}`;
	}
	return parentPath ? `${parentPath}/` : '/';
};


/**
 * Internal recursive walker called by findInvalidPages.
 * Starts from a child node (root is already skipped by the caller).
 */
const collectInvalidPages = (
	node: SitemapTreeData,
	parentPath: string,
	invalid: string[]
): void => {
	const segment = node.path || '';
	const currentPath = buildPath(parentPath, segment);

	if (node.pageAlias && !node.pageId && !isExternalUrl(segment)) {
		invalid.push(currentPath);
	}

	if (node.children) {
		node.children.forEach(child => {
			collectInvalidPages(child, currentPath, invalid);
		});
	}
};

/**
 * Find paths that have a pageAlias but are missing a pageId.
 * External URLs are excluded because they never have page IDs.
 *
 * Skips the root node and starts from its children with parentPath = '',
 * matching the path construction in transformSitemapToTreeView.
 */
const findInvalidPages = (root: SitemapTreeData): string[] => {
	const invalid: string[] = [];
	if (!root.children) return invalid;
	root.children.forEach(child => {
		collectInvalidPages(child, '', invalid);
	});
	return invalid;
};

/**
 * Recursively sort a SitemapTreeData's children alphabetically (folders first).
 */
const sortSitemapNode = (node: SitemapTreeData): SitemapTreeData => {
	if (!node.children?.length) return node;
	const sorted = [...node.children]
		.sort((a, b) => {
			// A node is treated as a folder only when it currently has children.
			// An empty category (no children in the sitemap) sorts alongside leaf pages.
			// This is intentional: without children, there is no visual distinction needed.
			const aIsFolder = (a.children?.length ?? 0) > 0 ? 0 : 1;
			const bIsFolder = (b.children?.length ?? 0) > 0 ? 0 : 1;
			if (aIsFolder !== bIsFolder) return aIsFolder - bIsFolder;
			return (a.path ?? '').localeCompare(b.path ?? '');
		})
		.map(sortSitemapNode);
	return { ...node, children: sorted };
};

/**
 * Dispatch a custom event to composer-ui via the events module.
 */
const dispatchToComposer = (eventName: string, detail = {}) => {
	events.dispatchAppEvent(APP_CONTAINER, eventName, detail);
};

// ────────────────────────────────────────────────────────────────────────────
// Hook
// ────────────────────────────────────────────────────────────────────────────
export const useSitemapData = () => {
	const [state, setState] = useState<SitemapState>(INITIAL_STATE);

	const fetchSitemapData = useCallback(async () => {
		setState(prev => ({ ...prev, isLoading: true, error: null }));
		try {
			const rootDTO = await SitemapApi.loadSitemapTree(LOCALE);
			const sortedRoot = sortSitemapNode(rootDTO);
			const invalidPages = findInvalidPages(sortedRoot);
			setState(prev => ({
				...prev,
				sitemapData: {
					tree: sortedRoot
				},
				invalidPages,
				isLoading: false,
				error: null
			}));
		} catch (err) {
			setState(prev => ({
				...prev,
				error: err instanceof Error ? err : new Error(String(err)),
				isLoading: false
			}));
		}
	}, []);

	useEffect(() => {
		const container = getContainer();
		if (!container) {
			console.error('useSitemapData: Container not found:', APP_CONTAINER);
			setState(prev => ({
				...prev,
				error: new Error('Container not found'),
				isLoading: false
			}));
			return undefined;
		}
		fetchSitemapData();
		return undefined;
	}, [fetchSitemapData]);

	// ── Action methods ────────────────────────────────────────────────────────

	const navigateToPage = useCallback((pagePath: string) => {
		dispatchToComposer(EVENTS.NAVIGATE_TO_PAGE, { pagePath });
	}, []);

	const selectPage = useCallback((pageDetails: PageData | null) => {
		if (pageDetails) {
			dispatchToComposer(EVENTS.SELECT_PAGE, pageDetails);
		}
	}, []);

	return {
		...state,
		navigateToPage,
		selectPage,
		refetch: fetchSitemapData
	};
};

export default useSitemapData;
